import type {
  CreateAppointmentRequest,
  InstitutionPositionView,
  PositionScopeType,
} from '@/types/organisation';

/**
 * The appointment form's rules, as data — no React, no i18n, no fetch.
 *
 * These are CLIENT-SIDE MIRRORS of what campusone-identity-service enforces on
 * POST /institutions/:id/appointments. Mirroring them here is error prevention, not
 * duplication of authority: the server remains the judge, but a form that lets you
 * fill in five fields and then bounces you on submit has wasted your time in a way a
 * disabled field never does. Each rule below names the server rule it mirrors.
 *
 * Errors are returned as machine-readable codes, not sentences. The page maps each
 * code to an i18n key (EN and FR); the tests assert against codes and stay
 * language-proof.
 */

export interface AppointmentDraft {
  personId: string;
  positionCode: string;
  scopeType: PositionScopeType | '';
  scopeReference: string;
  scopeDisplayName: string;
  campusId: string;
  acting: boolean;
  validFrom: string;
  validUntil: string;
  academicRankId: string;
  rankEvidenceReference: string;
  roleDefinitionId: string;
  reason: string;
}

export const EMPTY_DRAFT: AppointmentDraft = {
  personId: '',
  positionCode: '',
  scopeType: '',
  scopeReference: '',
  scopeDisplayName: '',
  campusId: '',
  acting: false,
  validFrom: '',
  validUntil: '',
  academicRankId: '',
  rankEvidenceReference: '',
  roleDefinitionId: '',
  reason: '',
};

export type AppointmentErrorCode =
  | 'personRequired'
  | 'positionRequired'
  | 'positionInactive'
  | 'scopeTypeRequired'
  | 'scopeTypeNotAssignable'
  | 'scopeReferenceRequired'
  | 'validFromRequired'
  | 'validUntilBeforeFrom'
  | 'actingNeedsEnd';

export type AppointmentErrors = Partial<
  Record<'personId' | 'positionCode' | 'scopeType' | 'scopeReference' | 'validFrom' | 'validUntil', AppointmentErrorCode>
>;

/**
 * Validates a draft against the chosen position.
 *
 * `position` is the row the code was picked from (null while nothing is picked). It
 * carries the two facts the rules need: whether the office is active here, and which
 * scope types it may be held over.
 */
export function validateAppointmentDraft(
  draft: AppointmentDraft,
  position: InstitutionPositionView | null,
): AppointmentErrors {
  const errors: AppointmentErrors = {};

  if (!draft.personId) errors.personId = 'personRequired';

  // Mirrors: positionCode must name a position that is active for this institution.
  if (!draft.positionCode) {
    errors.positionCode = 'positionRequired';
  } else if (position && !position.active) {
    errors.positionCode = 'positionInactive';
  }

  // Mirrors: scopeType must be one of the position's assignableScopeTypes.
  if (!draft.scopeType) {
    errors.scopeType = 'scopeTypeRequired';
  } else if (
    position &&
    position.assignableScopeTypes.length > 0 &&
    !position.assignableScopeTypes.includes(draft.scopeType)
  ) {
    errors.scopeType = 'scopeTypeNotAssignable';
  }

  // Mirrors: scopeReference is REQUIRED whenever the scope is narrower than the whole
  // institution — "Head of Department" of nothing in particular is not an appointment.
  if (draft.scopeType && draft.scopeType !== 'INSTITUTION' && !draft.scopeReference) {
    errors.scopeReference = 'scopeReferenceRequired';
  }

  if (!draft.validFrom) errors.validFrom = 'validFromRequired';

  // Mirrors: validUntil must fall strictly after validFrom. Dates are ISO yyyy-mm-dd,
  // so string comparison is date comparison — no timezone surprises from Date parsing.
  if (draft.validFrom && draft.validUntil && draft.validUntil <= draft.validFrom) {
    errors.validUntil = 'validUntilBeforeFrom';
  }

  // Mirrors: an acting appointment must carry an end date. An acting appointment that
  // never ends is a substantive appointment by another name.
  if (draft.acting && !draft.validUntil && !errors.validUntil) {
    errors.validUntil = 'actingNeedsEnd';
  }

  return errors;
}

/**
 * Builds the request body, and ONLY the request body.
 *
 * identity validates with forbidNonWhitelisted: one stray field is a 400. So this is
 * an explicit whitelist that drops empty optionals rather than sending them as ''
 * (an empty-string scopeReference would fail UUID validation server-side, and an
 * empty reason is not a reason).
 */
export function buildAppointmentPayload(draft: AppointmentDraft): CreateAppointmentRequest {
  if (!draft.scopeType) {
    throw new Error('buildAppointmentPayload called on an unvalidated draft');
  }
  const payload: CreateAppointmentRequest = {
    personId: draft.personId,
    positionCode: draft.positionCode,
    scopeType: draft.scopeType,
    validFrom: draft.validFrom,
  };
  if (draft.scopeType !== 'INSTITUTION' && draft.scopeReference) {
    payload.scopeReference = draft.scopeReference;
  }
  if (draft.scopeDisplayName) payload.scopeDisplayName = draft.scopeDisplayName;
  if (draft.campusId) payload.campusId = draft.campusId;
  if (draft.acting) payload.acting = true;
  if (draft.validUntil) payload.validUntil = draft.validUntil;
  if (draft.academicRankId) {
    payload.academicRankId = draft.academicRankId;
    if (draft.rankEvidenceReference) payload.rankEvidenceReference = draft.rankEvidenceReference;
  }
  if (draft.roleDefinitionId) payload.roleDefinitionId = draft.roleDefinitionId;
  if (draft.reason) payload.reason = draft.reason;
  return payload;
}
